import { HTMLAttributes, MutableRefObject, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import type { SwiperRef } from 'swiper/react';
import { useMediaQuery } from 'usehooks-ts';
import { ReviewItem } from '@/types';
import dynamic from 'next/dynamic';
import SplitText from '../SplitText';
import { useLazySwiper } from '@/hooks/use-swiper-lazy';
import useSlidesPerView from '@/hooks/use-slides-per-view';

const Card = dynamic(() => import('./Card'));
const ReviewsPagination = dynamic(() => import('./Pagination'));
interface Props extends HTMLAttributes<HTMLElement> {
    sliderTitle?: string;
    slides: ReviewItem[];
}

const NotSlider = ({ slides }: { slides: ReviewItem[] }) => {
    const [activeIndex, setActiveIndex] = useState(0);

    return (
        <div className="reviews-list">
            {slides.map((slide, i) => (
                <div
                    key={i}
                    className={classNames('review-slider-el__slide', {
                        'swiper-slide-active': activeIndex === i,
                    })}
                    onClick={() => {
                        setActiveIndex(i);
                        document.dispatchEvent(new Event('review-slider-slide-change'));
                    }}
                >
                    <Card {...slide} />
                </div>
            ))}
        </div>
    );
};

const NewCatalogSlider = ({ sliderTitle, slides, children, ...props }: Props) => {
    const { Swiper, SwiperSlide } = useLazySwiper({ useSwiper: true });
    const swiperRef = useRef<SwiperRef>();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isFirstRender, setIsFirstRender] = useState(true);
    const slidesPerView = useSlidesPerView({
        largeDesktop: 4,
        desktop: 3,
        largeTablet: 3,
        tablet: 2,
        defaultCount: 2.8,
    });
    const isDesktop = useMediaQuery('(min-width: 1200px)');
    const isMobile = useMediaQuery('(max-width: 767px)');

    useEffect(() => {
        const timeout = setTimeout(() => {
            setIsFirstRender(false);
        }, 1);

        return () => clearTimeout(timeout);
    }, []);

    return (
        <div {...props} className={classNames('reviews-slider', props.className)}>
            <div className="reviews-slider__top">
                {sliderTitle && (
                    <SplitText className="h1 reviews-slider__title" delay={0.1}>
                        {sliderTitle}
                    </SplitText>
                )}
                {!isFirstRender && slides.length > slidesPerView && (
                    <div className="new-catalog-slider__pagination">
                        <ReviewsPagination
                            length={slides.length}
                            page={currentIndex + 1}
                            onPaginationChange={(number) => {
                                if (isMobile && slides.length > 3) {
                                    const nextIndex = number - 1;
                                    swiperRef.current?.swiper.slideToLoop(nextIndex);
                                } else {
                                    if (currentIndex + 1 === slides.length) {
                                        if (number === 1) {
                                            swiperRef.current?.swiper.slideNext();
                                        } else {
                                            swiperRef.current?.swiper.slidePrev();
                                        }
                                    } else if (currentIndex === 0) {
                                        if (number === slides.length) {
                                            swiperRef.current?.swiper.slidePrev();
                                        } else {
                                            swiperRef.current?.swiper.slideNext();
                                        }
                                    } else {
                                        const nextIndex = number - 1;
                                        swiperRef.current?.swiper.slideToLoop(nextIndex);
                                    }
                                }
                            }}
                        />
                    </div>
                )}
            </div>
            <div className="reviews-slider-el-wrapper">
                {isFirstRender || slides.length <= slidesPerView ? (
                    <NotSlider slides={slides} />
                ) : (
                    <Swiper
                        className="new-catalog-slider-el reviews-slider-el"
                        slidesPerView={1}
                        initialSlide={isMobile && slides.length <= 3 ? 1 : 0}
                        ref={swiperRef as MutableRefObject<SwiperRef>}
                        onActiveIndexChange={(swiper) => {
                            setCurrentIndex(swiper.realIndex);
                            document.dispatchEvent(new Event('review-slider-slide-change'));
                        }}
                        spaceBetween={2}
                        slideToClickedSlide
                        speed={1000}
                        longSwipesRatio={0.01}
                        dir={isDesktop ? 'rtl' : 'ltr'}
                        threshold={20}
                        breakpoints={{
                            1921: {
                                slidesPerView: 4,
                            },
                            1200: {
                                slidesPerView: 4,
                            },
                            1024: {
                                slidesPerView: 3,
                            },
                            768: {
                                slidesPerView: 2,
                                loop: true,
                            },
                            320: {
                                slidesPerView: 3,
                                spaceBetween: 15,
                                centeredSlides: true,
                                loop: slides.length > 3,
                            },
                        }}
                    >
                        {slides.map((slide, i) => (
                            <SwiperSlide key={i} className="review-slider-el__slide">
                                <Card {...slide} />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                )}
            </div>
        </div>
    );
};

export default NewCatalogSlider;
